import React, { useState } from 'react';
import AppBar from '@mui/material/AppBar';
import Toolbar from '@mui/material/Toolbar';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import Drawer from '@mui/material/Drawer';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemText from '@mui/material/ListItemText';
import Box from '@mui/material/Box';
import useMediaQuery from '@mui/material/useMediaQuery';
import { useTheme } from '@mui/material/styles';
import { NavLink, useLocation } from 'react-router-dom';

/**
 * Navbar — fixed top navigation bar.
 * - Desktop (≥600px): inline nav links "Fanpage" and "Group"
 * - Mobile (<600px): hamburger icon → Drawer with nav links
 * Requirements: 1.1–1.7, 12.2
 */
const NAV_ITEMS = [
  { label: 'Fanpage', path: '/fanpages' },
  { label: 'Group', path: '/groups' },
];

/** Simple hamburger / close icon rendered with Unicode characters. */
const HamburgerIcon: React.FC<{ open: boolean }> = ({ open }) => (
  <Box
    component="span"
    aria-hidden="true"
    sx={{ fontSize: '1.5rem', lineHeight: 1 }}
  >
    {open ? '✕' : '☰'}
  </Box>
);

export const Navbar: React.FC = () => {
  const theme = useTheme();
  // Collapse to hamburger when viewport is narrower than 600px (xs breakpoint)
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [drawerOpen, setDrawerOpen] = useState(false);
  const location = useLocation();

  const toggleDrawer = () => setDrawerOpen((prev) => !prev);
  const closeDrawer = () => setDrawerOpen(false);

  return (
    // Req 12.2: semantic <header> landmark
    <header>
      {/* Req 1.1: fixed AppBar at top */}
      <AppBar position="fixed" component="div">
        <Toolbar>
          <Typography variant="h6" component="span" sx={{ flexGrow: 1, fontWeight: 700 }}>
            Page Group Web
          </Typography>

          {/* Contact button — always visible */}
          <Button
            href="https://www.facebook.com/premium69gr"
            target="_blank"
            rel="noopener noreferrer"
            variant="outlined"
            color="inherit"
            size="small"
            aria-label="Liên hệ qua Facebook (mở trong tab mới)"
            sx={{ mr: 1, borderColor: 'rgba(255,255,255,0.7)', whiteSpace: 'nowrap' }}
          >
            Liên hệ
          </Button>

          {isMobile ? (
            // Req 1.6: hamburger icon on screens < 600px
            <>
              <IconButton
                color="inherit"
                // Req 12.2: descriptive aria-label on hamburger button
                aria-label="Mở menu điều hướng"
                aria-expanded={drawerOpen}
                aria-controls="mobile-nav-drawer"
                onClick={toggleDrawer}
                edge="end"
              >
                <HamburgerIcon open={drawerOpen} />
              </IconButton>

              {/* Req 1.7: Drawer opens with nav items when hamburger is clicked */}
              <Drawer
                id="mobile-nav-drawer"
                anchor="right"
                open={drawerOpen}
                onClose={closeDrawer}
                PaperProps={{ sx: { width: 200 } }}
              >
                {/* Req 12.2: semantic <nav> with aria-label */}
                <nav aria-label="Điều hướng chính">
                  <List>
                    {NAV_ITEMS.map(({ label, path }) => {
                      const isActive = location.pathname === path;
                      return (
                        <ListItem key={path} disablePadding>
                          <ListItemButton
                            component={NavLink}
                            to={path}
                            onClick={closeDrawer}
                            selected={isActive}
                            // Req 1.4, 1.5: aria-current marks active route
                            aria-current={isActive ? 'page' : undefined}
                          >
                            <ListItemText primary={label} />
                          </ListItemButton>
                        </ListItem>
                      );
                    })}
                  </List>
                </nav>
              </Drawer>
            </>
          ) : (
            // Req 12.2: semantic <nav> with aria-label for desktop
            <nav aria-label="Điều hướng chính">
              <Box component="ul" sx={{ display: 'flex', gap: 1, listStyle: 'none', m: 0, p: 0 }}>
                {NAV_ITEMS.map(({ label, path }) => {
                  const isActive = location.pathname === path;
                  return (
                    <Box component="li" key={path}>
                      <Button
                        component={NavLink}
                        to={path}
                        color="inherit"
                        // Req 1.4, 1.5: aria-current marks active route
                        aria-current={isActive ? 'page' : undefined}
                        sx={{
                          // Req 1.4, 1.5: active link highlighted with bold + underline
                          fontWeight: isActive ? 700 : 400,
                          borderBottom: isActive ? '2px solid white' : '2px solid transparent',
                          borderRadius: 0,
                        }}
                      >
                        {label}
                      </Button>
                    </Box>
                  );
                })}
              </Box>
            </nav>
          )}
        </Toolbar>
      </AppBar>
      {/* Toolbar spacer so page content doesn't hide under the fixed AppBar */}
      <Toolbar />
    </header>
  );
};
